import { useRef, useState } from "react";
import { toast } from "sonner";
import { X, Upload, Sparkles, MessageCircle, Wrench } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";

const MAX_FILES = 3;
const MAX_FILE_BYTES = 1_200_000; // ~1.2 MB per image before base64
const PRODUCT_TYPES = [
  { value: "mirror", label: "Custom Mirror" },
  { value: "floorboard", label: "Custom Floorboards" },
  { value: "apparel", label: "Custom Apparel" },
  { value: "other", label: "Something else" },
];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function CustomQuote() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    product_type: "mirror", title: "",
    description: "", finish_color: "",
    motorcycle: "", budget: "", timeline: "",
  });
  const [refs, setRefs] = useState([]); // array of data URLs
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onFiles(e) {
    const files = Array.from(e.target.files || []);
    const room = MAX_FILES - refs.length;
    if (room <= 0) {
      toast.error(`Max ${MAX_FILES} reference images`);
      return;
    }
    const next = [];
    for (const f of files.slice(0, room)) {
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name} is larger than 1.2 MB`);
        continue;
      }
      try {
        next.push(await fileToDataUrl(f));
      } catch {
        toast.error(`Could not read ${f.name}`);
      }
    }
    if (next.length) setRefs((arr) => [...arr, ...next]);
    if (fileRef.current) fileRef.current.value = "";
  }

  function removeRef(i) {
    setRefs((arr) => arr.filter((_, idx) => idx !== i));
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.title || !form.description) {
      toast.error("Please fill the required fields");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/quote-requests", {
        ...form,
        reference_images: refs,
      });
      setDone(true);
      toast.success("Request received");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-24 text-center" data-testid="quote-success">
        <Sparkles className="w-12 h-12 text-[#c8102e] mx-auto" />
        <div className="bgc-eyebrow text-[#c8102e] mt-6">REQUEST RECEIVED</div>
        <h1 className="font-display text-6xl uppercase tracking-tight mt-3">{`You're On the Build List.`}</h1>
        <p className="text-[#d1d5db] mt-4 max-w-lg mx-auto">
          A real human from our garage will review your request and email you a quote within 48 hours. No bots. No spam.
        </p>
        <button onClick={() => { setDone(false); setForm({name:"",email:"",phone:"",product_type:"mirror",title:"",description:"",finish_color:"",motorcycle:"",budget:"",timeline:""}); setRefs([]); }}
                className="btn-ghost mt-10" data-testid="quote-new-button">
          SEND ANOTHER REQUEST
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20" data-testid="custom-page">
      <div className="bgc-eyebrow text-[#c8102e]">CUSTOM SHOP</div>
      <h1 className="font-display text-6xl md:text-7xl tracking-tight uppercase mt-3">
        Build It Your Way.
      </h1>
      <p className="text-[#9ca3af] mt-4 max-w-xl text-lg leading-relaxed">
        Got a one-of-a-kind design in your head? Tell us about it. We hand-build custom mirrors, floorboards, and apparel for women who don't settle for off-the-rack.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
        {[
          [<MessageCircle className="w-5 h-5" key="i1" />, "1. Tell us what you want", "Sketch, mood-board, or 5-second voice-memo's worth of words. We'll figure out the rest."],
          [<Wrench className="w-5 h-5" key="i2" />, "2. We mock it up", "You'll get a CAD render + price quote within 48 hours. No commitment yet."],
          [<Sparkles className="w-5 h-5" key="i3" />, "3. We build it", "Once you say go, 2-4 weeks from shop floor to your driveway."],
        ].map(([icon, t, body]) => (
          <div key={t} className="border border-white/10 p-6 hover:border-[#c8102e] transition-colors">
            <div className="text-[#c8102e]">{icon}</div>
            <p className="font-display text-base tracking-wider uppercase mt-3">{t}</p>
            <p className="text-sm text-[#9ca3af] mt-2 leading-relaxed">{body}</p>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-16">
        <div className="lg:col-span-2 space-y-8 border border-white/10 p-8 bg-[#0a0a0a]">
          <section>
            <h2 className="font-display text-2xl tracking-wider uppercase mb-5">Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input data-testid="quote-name" className="input-dark w-full" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </Field>
              <Field label="Email" required>
                <input data-testid="quote-email" type="email" className="input-dark w-full" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </Field>
              <Field label="Phone (optional)" className="md:col-span-2">
                <input data-testid="quote-phone" className="input-dark w-full" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </Field>
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-wider uppercase mb-5">The Build</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Type" required>
                <select data-testid="quote-type" className="input-dark w-full" value={form.product_type} onChange={(e) => set("product_type", e.target.value)}>
                  {PRODUCT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Working title" required>
                <input data-testid="quote-title" className="input-dark w-full" placeholder="e.g. Dragonfly bar-end mirror" value={form.title} onChange={(e) => set("title", e.target.value)} />
              </Field>
              <Field label="Describe it" required className="md:col-span-2">
                <textarea data-testid="quote-description" rows={5} className="input-dark w-full resize-none" placeholder="Shape, motif, vibe, anything we should know" value={form.description} onChange={(e) => set("description", e.target.value)} />
              </Field>
              <Field label="Finish / color">
                <input data-testid="quote-color" className="input-dark w-full" placeholder="e.g. anodized rose gold" value={form.finish_color} onChange={(e) => set("finish_color", e.target.value)} />
              </Field>
              <Field label="Motorcycle (year / make / model)">
                <input data-testid="quote-bike" className="input-dark w-full" placeholder="e.g. 2022 Road Glide ST" value={form.motorcycle} onChange={(e) => set("motorcycle", e.target.value)} />
              </Field>
              <Field label="Budget">
                <select data-testid="quote-budget" className="input-dark w-full" value={form.budget} onChange={(e) => set("budget", e.target.value)}>
                  <option value="">Not sure yet</option>
                  <option>Under $250</option>
                  <option>$250 – $500</option>
                  <option>$500 – $1,000</option>
                  <option>$1,000+</option>
                </select>
              </Field>
              <Field label="Timeline">
                <select data-testid="quote-timeline" className="input-dark w-full" value={form.timeline} onChange={(e) => set("timeline", e.target.value)}>
                  <option value="">Flexible</option>
                  <option>ASAP (rush fee may apply)</option>
                  <option>Within 4 weeks</option>
                  <option>Within 8 weeks</option>
                  <option>No rush</option>
                </select>
              </Field>
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl tracking-wider uppercase mb-5">Reference Images <span className="text-xs text-[#9ca3af] normal-case tracking-normal">(optional, up to {MAX_FILES})</span></h2>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="quote-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost" data-testid="quote-upload-button" disabled={refs.length >= MAX_FILES}>
              <Upload className="w-4 h-4" /> ADD IMAGES
            </button>
            {refs.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mt-4">
                {refs.map((src, i) => (
                  <div key={i} className="relative aspect-square border border-white/10" data-testid={`quote-ref-${i}`}>
                    <img src={src} alt={`ref-${i}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeRef(i)} className="absolute top-1 right-1 p-1 bg-black/80 border border-white/20 hover:border-[#c8102e]" data-testid={`quote-ref-${i}-remove`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-[#6b7280] mt-3">JPG / PNG · max 1.2 MB each.</p>
          </section>
        </div>

        <aside className="border border-white/10 p-6 h-fit bg-[#0a0a0a]">
          <h3 className="font-display text-xl tracking-wider uppercase">What you get</h3>
          <ul className="mt-5 space-y-3 text-sm text-[#d1d5db]">
            <li className="flex gap-2">✦ Free design consultation</li>
            <li className="flex gap-2">✦ CAD render before any payment</li>
            <li className="flex gap-2">✦ Hand-finished in our Arizona garage</li>
            <li className="flex gap-2">✦ 2-4 week build window</li>
            <li className="flex gap-2">✦ Lifetime hardware warranty</li>
          </ul>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-8" data-testid="quote-submit">
            {loading ? "SENDING…" : "REQUEST QUOTE"}
          </button>
          <p className="text-xs text-[#6b7280] mt-3 text-center">No commitment. We answer within 48 hrs.</p>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="bgc-eyebrow block mb-2">
        {label} {required && <span className="text-[#c8102e]">*</span>}
      </span>
      {children}
    </label>
  );
}
